import { ProductTypeEnum } from "@kanpla/api-contract";

import type { Product, StampCardDiscount } from "~/api/products/types";
import { resolveStampCardBaseDiscount } from "~/lib/sale-engine";

import { type KioskDisplayProductList } from "./types";
import { parseDisplayVariant } from "./parse-display-variant";
import { resolveDisplayUnitPrices } from "./resolve-display-totals";

type ParseDisplayProductListArgs = {
  product: Product;
  stampCardDiscount: StampCardDiscount;
  showPricesInclVat: boolean;
};

export function parseDisplayProductListRedeemable({
  product,
  stampCardDiscount,
  showPricesInclVat
}: ParseDisplayProductListArgs): KioskDisplayProductList {
  const productDiscounts: KioskDisplayProductList["discounts"] = [];

  let unitDisplayPrice = product.unitPriceInclVat;
  if (stampCardDiscount.stampCardRuleId) {
    productDiscounts.push({
      id: stampCardDiscount.stampCardRuleId,
      stampCardRuleId: stampCardDiscount.stampCardRuleId,
      type: "stamp-card"
    });

    // The reward only covers the base price, so the choices keep their full
    // price in the variant picker
    unitDisplayPrice -= resolveStampCardBaseDiscount({
      baseUnitPriceInclVat: product.unitPriceInclVat,
      discountType: stampCardDiscount.discountType,
      discount: stampCardDiscount.discount
    });
  }

  const partialBaseProduct: Omit<
    KioskDisplayProductList["product"],
    "type" | "deadline"
  > = {
    name: product.name,
    description: product.description,
    id: product.id,
    tags: product.tags ?? [],
    allergens: product.allergensDetailed,
    imageUrl: product.imageUrl,
    categoryId: product.categoryId,
    barcodeIds: product.barcodeIds,
    stamps: null,
    isLimitOver: false,
    disabled: false,
    stockStatus: "unlimited",
    remaining: null, // not needed in the UI for redeemable products
    orderMin: product.orderMin
  };

  let baseProduct: KioskDisplayProductList["product"] | null = null;

  if (
    product.type === ProductTypeEnum.VARIANTS &&
    product.variantIds &&
    product.variantIds.length > 0
  ) {
    baseProduct = {
      ...partialBaseProduct,
      type: "variant",
      variants: product.options.map((option) =>
        parseDisplayVariant({
          option,
          discountPercentage: null,
          showPricesInclVat
        })
      )
    };
  } else {
    baseProduct = {
      ...partialBaseProduct,
      type: "regular"
    };
  }

  return {
    product: baseProduct,
    discounts: productDiscounts,
    allowance: null,
    ...resolveDisplayUnitPrices({
      showPricesInclVat,
      unitPriceExclVat: product.unitPriceExclVat,
      unitPriceInclVat: product.unitPriceInclVat,
      discountedUnitPriceInclVat: unitDisplayPrice
    }),
    // servingBasis is a string enum at runtime, so the api shape already matches.
    nutrition: product.nutrition ?? null,
    co2Rating: product.environmental?.rating ?? null
  };
}
